import type { Key } from '@vizit/atoms';
import { ComboBox, type ComboBoxProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

interface FormComboBoxProps<T extends FieldValues, I extends object>
  extends Omit<
    ComboBoxProps<I>,
    'onSelectionChange' | 'selectedKey' | 'errorMessage' | 'isInvalid' | 'defaultSelectedKey'
  > {
  control: Control<T>;
  name: Path<T>;
  handleSelectedKey?: (key: Key | null) => void;
  controlledSelectedKey?: Key | null;
}

function FormComboBox<T extends FieldValues, I extends object>(
  props: FormComboBoxProps<T, I>,
  ref?: React.Ref<HTMLDivElement>,
) {
  const { control, name, children, isDisabled, className, controlledSelectedKey, handleSelectedKey, ...other } = props;

  const { field, fieldState, formState } = useController({ name, control });

  const defaultValue = React.useMemo(
    () => (formState?.defaultValues ? formState?.defaultValues[name] : undefined),
    [formState?.defaultValues, name],
  );

  const handleOnSelectionChange = (key: Key | null) => {
    field.onChange(key); // react form stuff
    handleSelectedKey?.(key);
  };

  return (
    <ComboBox
      className={cx('min-h-[100px]', className)}
      defaultSelectedKey={defaultValue}
      isDisabled={formState.isSubmitting || isDisabled}
      isInvalid={fieldState.invalid}
      onSelectionChange={handleOnSelectionChange}
      selectedKey={controlledSelectedKey || field.value}
      errorMessage={fieldState.error?.message}
      ref={ref}
      {...other}
    >
      {children}
    </ComboBox>
  );
}

export { FormComboBox };
export type { FormComboBoxProps };
